import React, { useEffect, useRef } from "react";
import { motion } from 'framer-motion';
import { FiGithub, FiLinkedin, FiArrowRight, FiMail } from 'react-icons/fi';
import { Badge, Button } from './ui';
import { trackSocialClick, trackSectionView, trackNavigationClick } from '../utils/mixpanel';
import userImg from '../assets/images/userimg_new.png';
import '../assets/styles/Main.scss';


function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const PARTICLE_COUNT = 72;
    const CONNECTION_DIST = 140;
    const BASE_SPEED = 0.35;
    const MOUSE_REPEL_DIST = 110;
    const MOUSE_REPEL_FORCE = 0.6;

    let animId: number;
    const mouse = { x: -999, y: -999 };

    // Each particle stores its natural base velocity so damping returns to it, not zero
    type Particle = { x: number; y: number; vx: number; vy: number; bvx: number; bvy: number; r: number };
    let particles: Particle[] = [];

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const init = () => {
      particles = Array.from({ length: PARTICLE_COUNT }, () => {
        const angle = Math.random() * Math.PI * 2;
        const spd   = BASE_SPEED * (0.6 + Math.random() * 0.8);
        const bvx   = Math.cos(angle) * spd;
        const bvy   = Math.sin(angle) * spd;
        return {
          x:   Math.random() * canvas.width,
          y:   Math.random() * canvas.height,
          vx:  bvx,
          vy:  bvy,
          bvx, bvy,
          r:   Math.random() * 1.4 + 0.8,
        };
      });
    };

    const getColors = () => {
      const isLight = document.querySelector('.main-container')?.classList.contains('light-mode') ?? false;
      return {
        isLight,
        dot:  isLight ? 'rgba(99, 102, 241, 0.35)' : 'rgba(255, 255, 255, 0.55)',
        line: isLight ? 'rgba(99, 102, 241,'        : 'rgba(255, 255, 255,',
      };
    };

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onMouseLeave = () => { mouse.x = -999; mouse.y = -999; };

    // Listen on the section, not canvas, so clicks still work on buttons
    const section = canvas.parentElement?.parentElement as HTMLElement;
    section?.addEventListener('mousemove', onMouseMove);
    section?.addEventListener('mouseleave', onMouseLeave);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const { dot, line, isLight } = getColors();

      for (const p of particles) {
        // Mouse repel
        const mdx = p.x - mouse.x;
        const mdy = p.y - mouse.y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < MOUSE_REPEL_DIST && mdist > 0) {
          const force = (MOUSE_REPEL_DIST - mdist) / MOUSE_REPEL_DIST * MOUSE_REPEL_FORCE;
          p.vx += (mdx / mdist) * force;
          p.vy += (mdy / mdist) * force;
        }

        // Speed cap
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (speed > 3) { p.vx = (p.vx / speed) * 3; p.vy = (p.vy / speed) * 3; }

        // Ease velocity back toward base velocity (not zero) — keeps auto-movement alive
        p.vx += (p.bvx - p.vx) * 0.012;
        p.vy += (p.bvy - p.vy) * 0.012;

        p.x += p.vx;
        p.y += p.vy;

        // Bounce off edges
        if (p.x < 0)              { p.x = 0;              p.vx = Math.abs(p.vx);  p.bvx = Math.abs(p.bvx);  }
        if (p.x > canvas.width)   { p.x = canvas.width;   p.vx = -Math.abs(p.vx); p.bvx = -Math.abs(p.bvx); }
        if (p.y < 0)              { p.y = 0;              p.vy = Math.abs(p.vy);  p.bvy = Math.abs(p.bvy);  }
        if (p.y > canvas.height)  { p.y = canvas.height;  p.vy = -Math.abs(p.vy); p.bvy = -Math.abs(p.bvy); }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = dot;
        ctx.fill();
      }

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx   = particles[i].x - particles[j].x;
          const dy   = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * (isLight ? 0.13 : 0.35);
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `${line} ${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(draw);
    };

    resize();
    init();
    draw();

    const ro = new ResizeObserver(() => { resize(); init(); });
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
      section?.removeEventListener('mousemove', onMouseMove);
      section?.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero__particles" />;
}

const stats = [
  { value: '8+', label: 'Years Experience' },
  { value: '70+', label: 'Happy Clients' },
  { value: '100+', label: 'Projects Completed' },
  { value: '98%', label: 'Success Rate' },
];

const socialLinks = [
  {
    icon: <FiGithub />,
    href: 'https://github.com/AmeerHamza112',
    label: 'GitHub'
  },
  {
    icon: <FiLinkedin />,
    href: 'https://www.linkedin.com/in/ameer-hamza-39604214b/',
    label: 'LinkedIn'
  },
];

function Main() {
  useEffect(() => {
    trackSectionView('main');
  }, []);

  const handleSocialClick = (platform: string, url: string) => {
    trackSocialClick(platform, url);
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      trackNavigationClick('contact');
    }
  };

  const scrollToProjects = () => {
    const element = document.getElementById('projects');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      trackNavigationClick('projects');
    }
  };

  return (
    <section className="hero" id="hero">
      {/* Background Effects */}
      <div className="hero__background">
        <div className="hero__orb hero__orb--1" />
        <div className="hero__orb hero__orb--2" />
        <div className="hero__grid" />
        <ParticleCanvas />
      </div>

      <div className="hero__container">
        <div className="hero__content">
          {/* Headline */}
          <motion.h1
            className="hero__title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Hi, I'm <span className="hero__name">Ameer Hamza</span>
          </motion.h1>

          <motion.h2
            className="hero__subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Senior Software Engineer
          </motion.h2>

          <motion.p
            className="hero__description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            I build mobile apps, architect scalable backend systems including Shopify embedded apps,
            and develop AI-powered solutions leveraging LLMs and Generative AI —
            delivering complete products from idea to production.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="hero__actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Button
              variant="primary"
              size="lg"
              onClick={scrollToContact}
              icon={<FiMail />}
            >
              Get in Touch
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={scrollToProjects}
              icon={<FiArrowRight />}
              iconPosition="right"
            >
              View Projects
            </Button>
          </motion.div>

          {/* Social Links */}
          <motion.div
            className="hero__socials"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hero__social-link"
                onClick={() => handleSocialClick(social.label, social.href)}
                aria-label={social.label}
              >
                {social.icon}
              </a>
            ))}
          </motion.div>
        </div>

        {/* Profile Image */}
        <motion.div
          className="hero__image-wrapper"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="hero__image-ring">
            <img
              src={userImg}
              alt="Ameer Hamza"
              className="hero__image"
            />
          </div>
        </motion.div>
      </div>

      {/* Stats Section */}
      <motion.div
        className="hero__stats"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        {stats.map((stat, index) => (
          <div key={stat.label} className="hero__stat">
            <motion.span
              className="hero__stat-value"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
            >
              {stat.value}
            </motion.span>
            <span className="hero__stat-label">{stat.label}</span>
          </div>
        ))}
      </motion.div>
    </section>
  );
}

export default Main;
